import type { FC, ReactElement } from "react";
import React, { useEffect } from "react";
import {
  Trans,
  useTracking,
  useTranslation,
} from "@edreamsodigeo/frontend-core";
import {
  Body,
  Box,
  Flex,
  Heading,
  List,
  ListItem,
  Modal,
  ModalCloseIcon,
  ModalContent,
  ModalHeader,
  ModalTitle,
  RawMoney,
  useFadeToggle,
} from "@edreamsodigeo/prisma-design-system";
import { Text } from "@edreamsodigeo/prisma-design-system/deprecated";

import { markerType } from "~/components/acquisition/SubscriptionManagerDesktop/common/FareSelectorSectionContentList";
import MembershipWithInstallmentsDisclaimer from "~/components/common/MembershipWithInstallmentsDisclaimer/MembershipWithInstallmentsDisclaimer";
import { getAutoRenewalPolicy } from "~/components/common/MembershipWithInstallmentsDisclaimer/tools/utils";
import type { Money } from "~/generated/graphql";
import {
  freeTrialDays,
  RenewalPrice,
  renewalTimeKey,
} from "~/tools/subscriptionUtils";
import { TRACKING_DATA } from "./config/tracking";
import type { BenefitsModalProps } from "./contract";
import { OnlineFunnel } from "~/constants/Funnel";

const Discount: FC<{ savings?: Money }> = ({ savings }): ReactElement => {
  if (!savings) return <></>;
  return (
    <strong>
      <RawMoney currency={savings?.currency} rounded>
        {savings?.amount}
      </RawMoney>
    </strong>
  );
};

export const BenefitsModal: FC<BenefitsModalProps> = ({
  offer,
  isOpen,
  setOpen,
  savings,
  funnel = OnlineFunnel.FLIGHTS,
  benefits: propBenefits,
  customKeys,
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const modalProps = useFadeToggle({ isOpen });

  const autoRenewalPolicy = getAutoRenewalPolicy(offer);

  const title = t("prime.benefits.popup.title");

  const subtitleKey =
    funnel === OnlineFunnel.HOTELS
      ? "results.transparentDisplayTooltipVersioned.hotels.perks.title"
      : "prime.benefits.popup.subtitle";

  const subtitle = (
    <Trans
      i18nKey={subtitleKey}
      components={{
        discount: <Discount savings={savings} />,
      }}
      values={{
        freeTrialDays: freeTrialDays(offer?.duration),
        freeTrialPeriod: t(
          "results.transparentDisplayTooltipVersioned.freeTrialPeriod.days",
        ),
      }}
    />
  );

  const checkForCustomKeys = (isAutoRenewalPolicy: boolean): string => {
    if (isAutoRenewalPolicy) {
      return (
        customKeys?.fifthPerk || "membershipWithInstallments.renewalMessage"
      );
    }

    return "prime.benefits.popup.perks.five";
  };
  const fifthPerkKey = checkForCustomKeys(!!autoRenewalPolicy);

  const fifthPerk = (
    <Trans
      i18nKey={fifthPerkKey}
      values={{
        renewalTime: t(renewalTimeKey(offer?.renewalDuration)),
      }}
      components={{
        membershipWithInstallmentsDisclaimer: (
          <MembershipWithInstallmentsDisclaimer
            autoRenewalPolicy={autoRenewalPolicy}
          />
        ),
        renewalPrice: (
          <RenewalPrice
            currency={offer?.renewalPrice?.currency}
            amount={offer?.renewalPrice?.amount}
          />
        ),
      }}
    />
  );

  const benefits = propBenefits ?? [
    t("prime.benefits.popup.perks.one"),
    t("prime.benefits.popup.perks.two"),
    t("prime.benefits.popup.perks.three"),
    t("prime.benefits.popup.perks.four", {
      freeTrialDays: freeTrialDays(offer?.duration),
      freeTrialPeriod: t("prime.renewalDuration.days"),
    }),
    fifthPerk,
  ];

  const onClickCloseButton = (): void => {
    trackEvent(TRACKING_DATA.onCloseModal);
    setOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(TRACKING_DATA.onLoadModal);
    }
  }, [isOpen, trackEvent]);

  return (
    <Modal
      {...modalProps}
      onClose={onClickCloseButton}
      size={"small"}
      sx={{
        paddingTop: "tiny",
        paddingBottom: "tiny",
        paddingRight: "tiny",
        paddingLeft: "tiny",
      }}
    >
      <ModalHeader>
        <ModalCloseIcon onClose={onClickCloseButton} />
        <ModalTitle color={"neutral.90"}>{title}</ModalTitle>
      </ModalHeader>

      <ModalContent
        sx={{
          paddingTop: "base",
          paddingBottom: "1x_big",
          paddingRight: "1x_big",
          paddingLeft: "1x_big",
        }}
      >
        <Flex flexDirection={"column"} gap={"big"}>
          <Heading type={"5"} color={"neutral.90"}>
            {subtitle}
          </Heading>
          <Body type={"small"} weight={"normal"} color={"neutral.80"}>
            <List size={"small"}>
              {benefits.map((benefit, idx) => (
                <ListItem marker={markerType.INCLUDED} key={`perksList-${idx}`}>
                  <Box pb={"1x_small"} pl={"1x_small"}>
                    <Text color={"neutral.80"}>{benefit}</Text>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Body>
        </Flex>
      </ModalContent>
    </Modal>
  );
};
